import React from 'react';

import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';

import { downloadCsv } from '../format';
import type { CsvImportError } from '../types';
import { type PickError, plainErrorCsvRows } from './csvImportErrors';
import { codeCls, metaCls } from './ui';

/** A CSV problem, optionally filed under a group (the order of an outbound row). */
export interface CsvErrorItem extends CsvImportError {
  group?: { code: string; sub?: string };
}

/**
 * One row per problem — Baris · Kolom (raw column + its Indonesian label) ·
 * Nilai di file · Masalah — with the count, "Download error CSV" and, when
 * the errors come in groups, a leading group column. Shared by every WMS CSV
 * import so a wrong file is explained the same way everywhere.
 */
export const CsvErrorTable: React.FC<{
  errors: readonly CsvErrorItem[];
  /** Indonesian label of a raw column, shown under its name. */
  columnLabel?: (field: string) => string | null;
  /** Name of the uploaded file (the error CSV is `errors-<fileName>`). */
  fileName?: string | null;
  /** Replaces the built-in download (plain row / column / value / reason). */
  onDownload?: () => void;
  /** Header of the leading group column; no column when absent. */
  groupLabel?: string;
  /** The list is narrowed to one group: its name, the total, and the way back. */
  narrowed?: { label: string; total: number; onClear: () => void } | null;
  /** Kept to the content height up to this (px). */
  maxHeight?: number;
}> = ({
  errors,
  columnLabel,
  fileName,
  onDownload,
  groupLabel,
  narrowed = null,
  maxHeight = 360,
}) => {
  const download =
    onDownload ??
    (() =>
      downloadCsv(
        `errors-${fileName ?? 'upload.csv'}`,
        plainErrorCsvRows(errors)
      ));
  return (
    <div className="overflow-hidden rounded-xl border border-stroke-soft-200">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stroke-soft-200 bg-bg-weak-50 px-3 py-2">
        <p className="text-[12px] text-text-sub-600">
          {narrowed ? (
            <>
              <span className="font-mono font-semibold text-text-strong-950">
                {narrowed.label}
              </span>{' '}
              · {errors.length} error ·{' '}
              <button
                type="button"
                className="font-semibold text-accent hover:underline"
                onClick={narrowed.onClear}
                data-testid="csv-error-all"
              >
                tampilkan semua ({narrowed.total})
              </button>
            </>
          ) : (
            `${errors.length} error memblokir import — satu baris = satu kolom yang salah`
          )}
        </p>
        <Button
          tone="primary"
          style="link"
          size="xs"
          className="text-xs font-semibold"
          onClick={download}
          data-testid="csv-error-download"
          leftIcon={
            <i className="ri-download-2-line text-[14px]" aria-hidden />
          }
        >
          Download error CSV
        </Button>
      </div>
      {errors.length === 0 ? (
        <p className={`${metaCls} px-3 py-6 text-center`}>
          Tidak ada error. Semua baris lolos pemeriksaan.
        </p>
      ) : (
        <div className="overflow-auto" style={{ maxHeight }}>
          <table
            className="w-full text-left text-[12px]"
            data-testid="csv-error-table"
          >
            <thead className="sticky top-0 bg-bg-weak-50 text-[11px] font-medium text-text-soft-400">
              <tr>
                {groupLabel && <th className="px-3 py-2">{groupLabel}</th>}
                <th className="px-3 py-2">Baris</th>
                <th className="px-3 py-2">Kolom</th>
                <th className="min-w-[120px] px-3 py-2">Nilai di file</th>
                <th className="px-3 py-2">Masalah</th>
              </tr>
            </thead>
            <tbody>
              {errors.map((e, i) => {
                const label = columnLabel?.(e.field) ?? null;
                return (
                  <tr
                    key={`${e.row}-${e.field}-${i}`}
                    className="border-t border-stroke-soft-200 align-top"
                    data-testid={`csv-error-row-${e.row}`}
                  >
                    {groupLabel && (
                      <td className="px-3 py-1.5">
                        {e.group ? (
                          <>
                            <span className={codeCls}>{e.group.code}</span>
                            {e.group.sub && (
                              <p
                                className={`${metaCls} max-w-[160px] truncate`}
                              >
                                {e.group.sub}
                              </p>
                            )}
                          </>
                        ) : (
                          <span className="italic text-text-soft-400">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-1.5 tabular-nums text-text-sub-600">
                      {e.row}
                    </td>
                    <td className="px-3 py-1.5">
                      <span className="font-mono text-[11px]">{e.field}</span>
                      {label && <p className={metaCls}>{label}</p>}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-text-sub-600">
                      {e.value ? (
                        <span className="break-all">{e.value}</span>
                      ) : (
                        <span className="italic text-text-soft-400">
                          kosong
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-(--state-error-dark)">
                      {e.message}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/**
 * A refusal of the whole upload, explained: what is wrong (title + the
 * server's / guard's words), which form fields it is about, what to do next.
 */
export const CsvFileProblem: React.FC<{
  problem: PickError;
  testId?: string;
}> = ({ problem, testId = 'csv-pick-error' }) => (
  <Alert
    status="error"
    appearance="stroke"
    size="lg"
    title={problem.title}
    data-testid={testId}
  >
    <div className="flex flex-col gap-2">
      {problem.detail && <p>{problem.detail}</p>}
      {problem.fields.length > 0 && (
        <table
          className="w-full text-left text-[12px]"
          data-testid={`${testId}-fields`}
        >
          <thead className="text-[11px] font-medium text-text-soft-400">
            <tr>
              <th className="py-1 pr-3">Field</th>
              <th className="py-1">Masalah</th>
            </tr>
          </thead>
          <tbody>
            {problem.fields.map((f) => (
              <tr key={f.field} className="border-t border-stroke-soft-200">
                <td className="py-1 pr-3">
                  {f.label}{' '}
                  <span className="font-mono text-[11px] text-text-soft-400">
                    {f.field}
                  </span>
                </td>
                <td className="py-1 text-(--state-error-dark)">{f.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {problem.hint && <p className={metaCls}>{problem.hint}</p>}
    </div>
  </Alert>
);
